import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Card from './components/Card';
import Loader from './components/Loader';
import Badge from './components/Badge';
import decodeHtml from './utils/decodeHtml';
import useProject from './hooks/useProject';
import { getProject, analyseUrl, getAuditByUrl, getIAAnalysis, createIAAnalysis, regenerateSuggestionBlock } from './api';
import Tab from './components/Tab';
import Button from './components/Button';
import Tooltip from './components/Tooltip';
import { useToast } from './ToastContext';
import ImagesWithoutAltModal from './components/ImagesWithoutAltModal';
import HeadingsStructureModal from './components/HeadingsStructureModal';
import {
  Lightbulb, RefreshCw, ExternalLink, Copy, Download,
  CheckCircle2, Circle, AlertTriangle, Shield, Globe,
  FileText, Image, Link2, Zap, Eye, EyeOff, ChevronRight, ChevronDown,
  Monitor, Smartphone, Search, Sparkles, List, Languages
} from 'lucide-react';
import { getRecommendations } from './utils/performanceRecommendations';
import LighthouseFindingList from './components/LighthouseFindingList';

function ScoreBadge({ value, size = 'lg' }) {
  const n = Math.round(Number(value) || 0);
  let color = 'text-green-600';
  let bg = 'bg-green-50 border-green-200';
  if (n < 90) { color = 'text-amber-600'; bg = 'bg-amber-50 border-amber-200'; }
  if (n < 60) { color = 'text-red-600'; bg = 'bg-red-50 border-red-200'; }
  if (n >= 100) { color = 'text-white'; bg = 'bg-green-500 border-green-500'; }
  const sizeClass = size === 'lg' ? 'text-4xl' : size === 'md' ? 'text-2xl' : 'text-base';
  return (
    <div className={`inline-flex items-baseline gap-1.5 px-4 py-2 rounded-xl border ${bg}`}>
      <span className={`font-bold ${sizeClass} ${color}`}>{n}</span>
      <span className={`${size === 'lg' ? 'text-lg' : 'text-sm'} ${color} opacity-60`}>/100</span>
    </div>
  );
}

function MetricCard({ label, value, status, detail, icon: Icon, action }) {
  let statusColor = 'border-green-200 bg-green-50';
  let statusText = 'text-green-700';
  if (status === 'warning') { statusColor = 'border-amber-200 bg-amber-50'; statusText = 'text-amber-700'; }
  if (status === 'error') { statusColor = 'border-red-200 bg-red-50'; statusText = 'text-red-700'; }
  if (status === 'neutral') { statusColor = 'border-blue-gray-200 bg-blue-gray-50'; statusText = 'text-blue-gray-700'; }
  return (
    <div className={`rounded-xl p-4 border ${statusColor} flex flex-col gap-1`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} className={statusText} />}
        <span className="text-sm text-blue-gray-600">{label}</span>
      </div>
      <div className={`text-base font-semibold ${statusText}`}>{value}</div>
      {detail && <div className="text-xs text-blue-gray-500 mt-0.5">{detail}</div>}
      {action && <div className="mt-1.5">{action}</div>}
    </div>
  );
}

function CWVMetric({ label, value, score, unit }) {
  let color = 'text-green-600 bg-green-50 border-green-200';
  if (score !== null && score !== undefined) {
    if (score < 0.5) color = 'text-red-600 bg-red-50 border-red-200';
    else if (score < 0.9) color = 'text-amber-600 bg-amber-50 border-amber-200';
  }
  return (
    <div className={`rounded-lg p-3 border ${color} text-center`}>
      <div className="text-xs text-blue-gray-600 mb-1">{label}</div>
      <div className="text-lg font-bold">{value || '—'}</div>
      {unit && <div className="text-xs opacity-60">{unit}</div>}
    </div>
  );
}

function formatRenderableValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    // Handle redirect chain items serialized as { from, to }.
    if (value.from || value.to) {
      const from = value.from ? String(value.from) : '';
      const to = value.to ? String(value.to) : '';
      return from && to ? `${from} -> ${to}` : (from || to || '');
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export default function ProjectSEODetail() {
  const { slug, encodedUrl } = useParams();
  const [searchParams] = useSearchParams();
  const { project, loading: projectLoading } = useProject(slug);
  const { showToast } = useToast();
  const [contextData, setContextData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [iaData, setIaData] = useState(null);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState('');
  const [regeneratingBlock, setRegeneratingBlock] = useState(null); // 'titre' | 'meta_description' | 'h1'
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [completedRecommendations, setCompletedRecommendations] = useState(() => {
    try {
      const saved = localStorage.getItem(`seo-reco-${slug}-${encodedUrl}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  // Regroupement 6 -> 3 onglets (compat anciens liens ?tab=technique/performance/ia_*).
  const TAB_ALIASES = {
    technique: 'diagnostic',
    performance: 'diagnostic',
    diagnostic: 'diagnostic',
    ia_resume: 'ia',
    ia_structure: 'ia',
    ia: 'ia',
    ia_plan: 'plan',
    suggestions: 'plan',
    plan: 'plan',
  };
  const initialTab = TAB_ALIASES[searchParams.get('tab')] || 'diagnostic';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [perfLoading, setPerfLoading] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [showHeadingsModal, setShowHeadingsModal] = useState(false);

  const toggleRecommendation = (key, index) => {
    const recommendationKey = `${key}-${index}`;
    setCompletedRecommendations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recommendationKey)) newSet.delete(recommendationKey);
      else newSet.add(recommendationKey);
      try { localStorage.setItem(`seo-reco-${slug}-${encodedUrl}`, JSON.stringify([...newSet])); } catch { }
      return newSet;
    });
  };

  const detailTabs = [
    { key: 'diagnostic', label: 'Diagnostic (SEO & performance)' },
    { key: 'ia', label: 'Analyse IA' },
    { key: 'plan', label: "Plan d'action & suggestions" },
  ];

  useEffect(() => {
    if (!slug || !encodedUrl) return;
    let cancelled = false;
    async function fetchContextInfo() {
      setLoading(true);
      setError('');
      try {
        const projectData = await getProject(slug);
        let baseUrl = projectData.url;
        if (!baseUrl) baseUrl = `https://${projectData.name}`;
        if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) baseUrl = `https://${baseUrl}`;
        baseUrl = baseUrl.replace(/\/+$/, '');

        let fullUrl;
        if (encodedUrl === 'home') {
          fullUrl = baseUrl;
        } else {
          let decodedPath = encodedUrl.replace(/___/g, '/');
          try { decodedPath = decodeURIComponent(decodedPath); } catch { }
          let pagePath = decodedPath.startsWith('/') ? decodedPath : '/' + decodedPath;
          fullUrl = baseUrl + pagePath;
        }
        fullUrl = fullUrl.replace(/\/+$/, '');
        try { new URL(fullUrl); } catch (urlError) { throw new Error(`URL invalide: ${fullUrl}`); }

        let result = null;
        try { result = await getAuditByUrl(slug, fullUrl); } catch (e) {
          if (e.response?.status !== 404) console.warn('Erreur audit existant:', e);
        }
        if (!result) result = await analyseUrl(slug, fullUrl);
        if (!cancelled) setContextData(result);
      } catch (e) {
        if (!cancelled) {
          setContextData(null);
          const errorMessage = e.response?.data?.detail || e.message || 'Erreur inconnue';
          setError(`Impossible de récupérer les informations de la page: ${errorMessage}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchContextInfo();
    return () => { cancelled = true; };
  }, [slug, encodedUrl]);

  const handleAnalysePerformance = async () => {
    if (!slug || !contextData?.url || perfLoading) return;
    setPerfLoading(true);
    try {
      const result = await analyseUrl(slug, contextData.url, false);
      if (result) {
        setContextData(result);
        showToast('Analyse performance terminée', 'success');
      } else {
        showToast('Aucune donnée reçue après l\'analyse', 'error');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erreur lors de l\'analyse';
      showToast(msg, 'error');
    } finally {
      setPerfLoading(false);
    }
  };

  useEffect(() => {
    if (!contextData?.url || !slug) return;
    let cancelled = false;
    async function fetchSavedAnalysis() {
      try {
        const savedData = await getIAAnalysis(slug, contextData.url);
        if (!cancelled) setIaData(savedData);
      } catch { }
    }
    fetchSavedAnalysis();
    return () => { cancelled = true; };
  }, [contextData?.url, slug]);

  const handleAnalyseGemini = () => {
    if (!contextData) { showToast('Aucune donnée de page disponible', 'error'); return; }
    setIaData(null); setSaveSuccess(false); setIaError(''); setIaLoading(true);
    (async () => {
      try {
        if (!contextData.url) throw new Error('URL non disponible');
        const result = await createIAAnalysis(slug, { url: contextData.url });
        setIaData(result); setSaveSuccess(true);
        showToast('Analyse IA terminée !', 'success');
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (e) {
        let msg = 'Erreur lors de l\'analyse IA';
        if (e.response) msg = e.response.data?.detail || e.response.statusText || msg;
        else if (e.request) msg = 'Pas de réponse du serveur.';
        else msg = e.message || msg;
        setIaError(msg); showToast(`Erreur: ${msg}`, 'error');
      } finally { setIaLoading(false); }
    })();
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copié !', 'success');
  };

  const getBlockLabel = (block) => (
    block === 'titre' ? 'de titre' : block === 'meta_description' ? 'de meta description' : 'de H1'
  );

  const handleRegenerateBlock = async (block) => {
    if (!contextData?.url) { showToast('URL non disponible', 'error'); return; }
    setRegeneratingBlock(block);
    try {
      const result = await regenerateSuggestionBlock(slug, { url: contextData.url, block });
      setIaData(result);
      showToast(`Suggestions ${getBlockLabel(block)} régénérées`, 'success');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erreur lors de la régénération';
      showToast(msg, 'error');
    } finally {
      setRegeneratingBlock(null);
    }
  };

  const handleGenerateMainTagSuggestions = async (block) => {
    if (!contextData?.url) { showToast('URL non disponible', 'error'); return; }
    if (regeneratingBlock) return;
    setRegeneratingBlock(block);
    try {
      if (!iaData) {
        const initialAnalysis = await createIAAnalysis(slug, { url: contextData.url });
        setIaData(initialAnalysis);
        showToast(`Suggestions ${getBlockLabel(block)} générées`, 'success');
        return;
      }
      const result = await regenerateSuggestionBlock(slug, { url: contextData.url, block });
      setIaData(result);
      showToast(`Suggestions ${getBlockLabel(block)} régénérées`, 'success');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erreur lors de la génération';
      showToast(msg, 'error');
    } finally {
      setRegeneratingBlock(null);
    }
  };

  const perfRecommendations = useMemo(() => getRecommendations(contextData?.pagespeed_raw), [contextData?.pagespeed_raw]);
  const cwv = useMemo(() => {
    const audits = contextData?.pagespeed_raw?.lighthouseResult?.audits || {};
    return {
      lcp: audits['largest-contentful-paint'] || null,
      fid: audits['max-potential-fid'] || null,
      cls: audits['cumulative-layout-shift'] || null,
      tbt: audits['total-blocking-time'] || null,
      si: audits['speed-index'] || null,
      fcp: audits['first-contentful-paint'] || null,
    };
  }, [contextData?.pagespeed_raw]);

  if (loading || projectLoading) {
    return (
      <div className="w-full py-12">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-blue-gray-100 rounded w-1/3" />
          <div className="h-32 bg-blue-gray-100 rounded-xl" />
          <div className="h-64 bg-blue-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full py-8">
        <div className="text-center py-12 bg-red-50 rounded-xl border border-red-200">
          <AlertTriangle size={32} className="text-red-500 mx-auto mb-3" />
          <div className="text-red-700 font-semibold mb-2">Erreur</div>
          <div className="text-red-600 text-sm max-w-md mx-auto">{error}</div>
        </div>
      </div>
    );
  }

  const d = contextData;
  const am = d?.advanced_metrics || {};
  const titleLen = d?.title_length ?? (d?.title?.length || 0);
  const metaLen = d?.meta_length ?? (d?.meta_description?.length || 0);
  const h1Count = d?.nb_h1 ?? (d?.h1 ? 1 : 0);

  const suggestions = iaData?.suggestions || {};
  const titreList = Array.isArray(suggestions.titre) ? suggestions.titre : [];
  const metaList = Array.isArray(suggestions.meta_description) ? suggestions.meta_description : [];
  const h1List = Array.isArray(suggestions.h1) ? suggestions.h1 : [];
  const altList = Array.isArray(suggestions.alt_images) ? suggestions.alt_images : [];

  const hasPerf = d?.pagespeed_average > 0;

  return (
    <div className="w-full flex flex-col gap-6 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mt-4">
        <div className="min-w-0 flex-1">
          <button onClick={() => window.history.back()} className="text-sm text-blue-gray-500 hover:text-accent mb-2 flex items-center gap-1">
            ← Retour
          </button>
          <h1 className="font-heading text-xl font-bold text-blue-gray-900 truncate">{d?.title || 'Analyse de l\'URL'}</h1>
          <a href={d?.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-gray-500 hover:text-accent truncate block mt-0.5">
            {d?.url?.replace(/^https?:\/\//, '')} <ExternalLink size={12} className="inline ml-1" />
          </a>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {d?.url && (
            <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-gray-500 hover:text-accent flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-blue-gray-100 transition-colors">
              <ExternalLink size={16} /> Voir
            </a>
          )}
        </div>
      </div>

      {/* Scores overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="!p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-blue-gray-500 mb-1">Score SEO</div>
              <ScoreBadge value={d?.score_global} size="lg" />
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div className="text-blue-gray-500">Title</div><div className="font-medium text-right">{Math.round(d?.score_title || 0)}%</div>
              <div className="text-blue-gray-500">Meta</div><div className="font-medium text-right">{Math.round(d?.score_meta || 0)}%</div>
              <div className="text-blue-gray-500">H1</div><div className="font-medium text-right">{Math.round(d?.score_h1 || 0)}%</div>
              <div className="text-blue-gray-500">Images</div><div className="font-medium text-right">{Math.round(d?.score_images || 0)}%</div>
              <div className="text-blue-gray-500">Canonical</div><div className="font-medium text-right">{Math.round(d?.score_canonical || 0)}%</div>
            </div>
          </div>
        </Card>

        <Card className="!p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-blue-gray-500 mb-1">Performance</div>
              {hasPerf ? (
                <ScoreBadge value={d.pagespeed_average} size="lg" />
              ) : (
                <div className="text-blue-gray-400 text-sm italic mt-2">Non analysée</div>
              )}
            </div>
            {hasPerf && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="flex items-center gap-1 text-blue-gray-500"><Monitor size={12} /> Desktop</div>
                <div className="font-medium text-right">{Math.round(d.pagespeed_performance_desktop || 0)}</div>
                <div className="flex items-center gap-1 text-blue-gray-500"><Smartphone size={12} /> Mobile</div>
                <div className="font-medium text-right">{Math.round(d.pagespeed_performance_mobile || 0)}</div>
                <div className="text-blue-gray-500">Accessibilité</div>
                <div className="font-medium text-right">{Math.round(d.pagespeed_accessibility || 0)}</div>
                <div className="text-blue-gray-500">SEO</div>
                <div className="font-medium text-right">{Math.round(d.pagespeed_seo || 0)}</div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tab active={activeTab} onChange={setActiveTab} tabData={detailTabs} />

      {/* ==================== TAB: SEO Technique ==================== */}
      {activeTab === 'diagnostic' && (
        <div className="flex flex-col gap-6">
          {/* Balises principales */}
          <Card>
            <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-4 flex items-center gap-2">
              <FileText size={18} className="text-accent" /> Balises principales
            </h3>
            <div className="space-y-4">
              <div className="bg-blue-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-gray-600">Titre</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleGenerateMainTagSuggestions('titre')}
                      disabled={!!regeneratingBlock}
                      className="text-xs text-accent hover:text-accent/80 hover:bg-accent/5 rounded-lg py-1 px-2 transition-colors flex items-center gap-1 font-medium border border-accent/20 disabled:opacity-60 disabled:cursor-not-allowed"
                      title="Générer de nouveaux titres optimisés"
                    >
                      <RefreshCw size={12} className={regeneratingBlock === 'titre' ? 'animate-spin' : ''} />
                      {regeneratingBlock === 'titre' ? 'Génération...' : 'Générer'}
                    </button>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${titleLen >= 50 && titleLen <= 60 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {titleLen} car. {titleLen < 50 ? '(court)' : titleLen > 60 ? '(long)' : '(optimal)'}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-blue-gray-500 mb-1">Recommandation Google : 50–60 caractères</div>
                <div className="text-blue-gray-900">{d?.title || <span className="text-blue-gray-400 italic">Manquant</span>}</div>
                {titreList.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {titreList.slice(0, 2).map((item, i) => (
                      <div key={`title-suggestion-${i}`} className="flex items-start justify-between gap-2 bg-white rounded-md border border-blue-gray-200 px-2.5 py-2">
                        <div className="text-xs text-blue-gray-700">{item}</div>
                        <button onClick={() => handleCopy(item)} className="p-1 rounded text-blue-gray-400 hover:text-accent hover:bg-accent/5 transition-colors flex-shrink-0" title="Copier">
                          <Copy size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-blue-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-gray-600">Meta description</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleGenerateMainTagSuggestions('meta_description')}
                      disabled={!!regeneratingBlock}
                      className="text-xs text-accent hover:text-accent/80 hover:bg-accent/5 rounded-lg py-1 px-2 transition-colors flex items-center gap-1 font-medium border border-accent/20 disabled:opacity-60 disabled:cursor-not-allowed"
                      title="Générer de nouvelles meta descriptions optimisées"
                    >
                      <RefreshCw size={12} className={regeneratingBlock === 'meta_description' ? 'animate-spin' : ''} />
                      {regeneratingBlock === 'meta_description' ? 'Génération...' : 'Générer'}
                    </button>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${metaLen >= 150 && metaLen <= 160 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {metaLen} car. {metaLen < 150 ? '(court)' : metaLen > 160 ? '(long)' : '(optimal)'}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-blue-gray-500 mb-1">Recommandation Google : 150–160 caractères</div>
                <div className="text-blue-gray-900 text-sm">{d?.meta_description || <span className="text-blue-gray-400 italic">Manquante</span>}</div>
                {metaList.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {metaList.slice(0, 2).map((item, i) => (
                      <div key={`meta-suggestion-${i}`} className="flex items-start justify-between gap-2 bg-white rounded-md border border-blue-gray-200 px-2.5 py-2">
                        <div className="text-xs text-blue-gray-700">{item}</div>
                        <button onClick={() => handleCopy(item)} className="p-1 rounded text-blue-gray-400 hover:text-accent hover:bg-accent/5 transition-colors flex-shrink-0" title="Copier">
                          <Copy size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-blue-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-gray-600">H1</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${h1Count === 1 ? 'bg-green-100 text-green-700' : h1Count === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {h1Count === 0 ? 'Manquant' : h1Count === 1 ? '1 H1 (OK)' : `${h1Count} H1 (trop)`}
                  </span>
                </div>
                <div className="text-blue-gray-900">{d?.h1 || <span className="text-blue-gray-400 italic">Manquant</span>}</div>
              </div>
              <div className="bg-blue-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-gray-600">URL Canonique</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${d?.canonical_url ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {d?.canonical_url ? 'Présente' : 'Manquante'}
                  </span>
                </div>
                {d?.canonical_url && <div className="text-blue-gray-700 text-sm font-mono break-all">{d.canonical_url}</div>}
              </div>
            </div>
          </Card>

          {/* Métriques techniques */}
          <Card>
            <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-4 flex items-center gap-2">
              <Globe size={18} className="text-accent" /> Métriques techniques
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <MetricCard
                icon={Image}
                label="Images sans alt"
                value={`${d?.nb_images_without_alt ?? 0} / ${d?.nb_images ?? 0}`}
                status={(d?.nb_images_without_alt ?? 0) === 0 ? 'ok' : 'error'}
                action={(d?.nb_images_without_alt ?? 0) > 0 ? (
                  <button
                    onClick={() => setShowImagesModal(true)}
                    className="w-full text-xs text-accent hover:text-accent/80 hover:bg-accent/10 rounded-lg py-1.5 px-2 transition-colors flex items-center justify-center gap-1 font-medium border border-accent/20"
                  >
                    <Search size={12} /> Voir les {d.nb_images_without_alt} image{d.nb_images_without_alt > 1 ? 's' : ''}
                  </button>
                ) : undefined}
              />
              <MetricCard
                icon={Globe}
                label="Attribut lang"
                value={d?.lang || 'Manquant'}
                status={d?.lang ? 'ok' : 'warning'}
              />
              <MetricCard
                icon={Languages}
                label="Site multilingue"
                value={am.is_multilingual ? 'Oui' : 'Non'}
                status="neutral"
                detail={am.is_multilingual && Array.isArray(am.hreflang_alternates) && am.hreflang_alternates.length > 0
                  ? `Langues : ${[...new Set(am.hreflang_alternates.map(a => a.lang).filter(l => l !== 'x-default'))].join(', ')}`
                  : undefined}
              />
              <MetricCard
                icon={Shield}
                label="HTTPS"
                value={am.is_https ? 'Activé' : 'Non'}
                status={am.is_https ? 'ok' : 'error'}
              />
              <MetricCard
                icon={Eye}
                label="Viewport"
                value={am.viewport_missing ? 'Manquant' : 'Présent'}
                status={am.viewport_missing ? 'error' : 'ok'}
              />
              {am.noindex !== undefined && (
                <MetricCard
                  icon={EyeOff}
                  label="Indexation"
                  value={am.noindex ? 'noindex' : 'Indexable'}
                  status={am.noindex ? 'error' : 'ok'}
                  detail={am.nofollow ? 'nofollow activé' : undefined}
                />
              )}
              {am.robots_content && (
                <MetricCard
                  icon={FileText}
                  label="Robots"
                  value={am.robots_content || 'Aucun'}
                  status="neutral"
                />
              )}
              {am.text_ratio !== undefined && (
                <MetricCard
                  icon={FileText}
                  label="Ratio texte/HTML"
                  value={`${am.text_ratio?.toFixed?.(1) || 0}%`}
                  status={am.text_ratio >= 20 ? 'ok' : 'warning'}
                  detail={am.text_ratio < 20 ? 'Recommandé > 20%' : undefined}
                />
              )}
              {d?.structured_data !== undefined && (
                <MetricCard
                  icon={Link2}
                  label="Données structurées"
                  value={d.structured_data?.length > 0 ? `${d.structured_data.length} type(s)` : 'Aucune'}
                  status={d.structured_data?.length > 0 ? 'ok' : 'warning'}
                />
              )}
              {am.word_count > 0 && (
                <MetricCard
                  icon={FileText}
                  label="Mots sur la page"
                  value={`${am.word_count} mots`}
                  status={am.word_count >= 300 ? 'ok' : am.word_count >= 100 ? 'warning' : 'error'}
                  detail={am.word_count < 300 ? 'Recommandé : 300+ mots' : undefined}
                />
              )}
              {(am.nb_internal_links !== undefined || am.nb_external_links !== undefined) && (
                <MetricCard
                  icon={Link2}
                  label="Liens"
                  value={`${am.nb_internal_links ?? 0} int. / ${am.nb_external_links ?? 0} ext.`}
                  status="neutral"
                  detail="Liens internes / externes"
                />
              )}
            </div>
          </Card>

          {/* Modals for images & headings */}
          <ImagesWithoutAltModal
            open={showImagesModal}
            onClose={() => setShowImagesModal(false)}
            images={am.images_without_alt_list || []}
            totalImages={d?.nb_images ?? 0}
            onCopy={(text) => showToast('Copié !', 'success')}
            slug={slug}
            pageUrl={d?.url}
            onError={(msg) => showToast(msg, 'error')}
          />

          {/* Structure headings */}
          {d?.headings && Object.keys(d.headings).length > 0 && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base font-semibold text-blue-gray-900">Structure des headings</h3>
                <button
                  onClick={() => setShowHeadingsModal(true)}
                  className="text-xs text-accent hover:text-accent/80 hover:bg-accent/5 rounded-lg py-1.5 px-3 transition-colors flex items-center gap-1 font-medium"
                >
                  <Eye size={14} /> Voir l'architecture
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(d.headings).map(([level, count]) => (
                  <span key={level} className="text-sm bg-blue-gray-100 px-3 py-1.5 rounded-lg border border-blue-gray-200 font-mono">
                    {level.toUpperCase()}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </Card>
          )}

          <HeadingsStructureModal
            open={showHeadingsModal}
            onClose={() => setShowHeadingsModal(false)}
            headingsList={am.headings_list || []}
            headingsCounts={d?.headings || {}}
          />

          {/* Security headers */}
          {d?.missing_security_headers && d.missing_security_headers.length > 0 && (
            <Card>
              <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-3 flex items-center gap-2">
                <Shield size={18} className="text-amber-500" /> Headers de sécurité manquants
              </h3>
              <div className="flex flex-wrap gap-2">
                {d.missing_security_headers.map((h, i) => (
                  <span key={i} className="text-sm bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg border border-amber-200 font-mono">
                    {h}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* Redirect chain */}
          {am.redirect_chain && am.redirect_chain.length > 0 && (
            <Card>
              <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-3">Chaîne de redirections</h3>
              <div className="space-y-1">
                {am.redirect_chain.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {i > 0 && <ChevronRight size={14} className="text-blue-gray-400" />}
                    <span className="font-mono text-blue-gray-700 break-all">{formatRenderableValue(entry)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Open Graph & Twitter */}
          {((d?.og_tags && Object.keys(d.og_tags).length > 0) || (d?.twitter_tags && Object.keys(d.twitter_tags).length > 0)) ? (
            <Card>
              <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">Balises sociales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {d.og_tags && Object.keys(d.og_tags).length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-blue-gray-600 mb-2">Open Graph</div>
                    <div className="space-y-2">
                      {Object.entries(d.og_tags).map(([key, value]) => (
                        <div key={key} className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <div className="text-xs font-medium text-blue-700 mb-0.5">og:{key}</div>
                          <div className="text-sm text-blue-gray-900 break-all">{key === 'image' ? <img src={value} alt={key} className="w-full h-auto rounded-md mt-1" /> : value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {d.twitter_tags && Object.keys(d.twitter_tags).length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-blue-gray-600 mb-2">Twitter Cards</div>
                    <div className="space-y-2">
                      {Object.entries(d.twitter_tags).map(([key, value]) => (
                        <div key={key} className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <div className="text-xs font-medium text-blue-700 mb-0.5">twitter:{key}</div>
                          <div className="text-sm text-blue-gray-900 break-all">{key === 'image' ? <img src={value} alt={key} className="w-full h-auto rounded-md mt-1" /> : value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card>
              <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-2">Balises sociales</h3>
              <div className="text-sm text-blue-gray-400 italic">Aucune balise Open Graph ou Twitter Card détectée.</div>
            </Card>
          )}
        </div>
      )}

      {/* ==================== TAB: Performance ==================== */}
      {activeTab === 'diagnostic' && (
        <div className="flex flex-col gap-6">
          {perfLoading ? (
            <>
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div className="h-5 w-48 bg-blue-gray-100 rounded animate-pulse" />
                  <div className="h-9 w-36 bg-blue-gray-100 rounded-lg animate-pulse" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="rounded-lg p-4 border border-blue-gray-100 bg-blue-gray-50/50">
                      <div className="h-3 w-12 bg-blue-gray-200 rounded animate-pulse mb-2 mx-auto" />
                      <div className="h-6 w-14 bg-blue-gray-200 rounded animate-pulse mx-auto" />
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <div className="h-5 w-40 bg-blue-gray-100 rounded animate-pulse mb-4" />
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="rounded-xl p-4 border border-blue-gray-100 bg-blue-gray-50/50 text-center">
                      <div className="h-3 w-16 bg-blue-gray-200 rounded animate-pulse mb-2 mx-auto" />
                      <div className="h-8 w-12 bg-blue-gray-200 rounded animate-pulse mx-auto" />
                    </div>
                  ))}
                </div>
              </Card>
              <div className="flex items-center justify-center gap-2 py-4 text-blue-gray-500 text-sm">
                <span className="inline-block w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                Analyse PageSpeed en cours (mobile + desktop)…
              </div>
            </>
          ) : hasPerf ? (
            <>
              {/* Core Web Vitals */}
              <Card>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <h3 className="font-heading text-base font-semibold text-blue-gray-900 flex items-center gap-2">
                    <Zap size={18} className="text-accent" /> Core Web Vitals
                  </h3>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleAnalysePerformance}
                    disabled={perfLoading}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <RefreshCw size={14} className={perfLoading ? 'animate-spin' : ''} />
                    {perfLoading ? 'Analyse en cours...' : 'Réanalyser la performance'}
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <CWVMetric label="LCP" value={cwv.lcp?.displayValue} score={cwv.lcp?.score} />
                  <CWVMetric label="FID" value={cwv.fid?.displayValue} score={cwv.fid?.score} />
                  <CWVMetric label="CLS" value={cwv.cls?.displayValue} score={cwv.cls?.score} />
                  <CWVMetric label="TBT" value={cwv.tbt?.displayValue} score={cwv.tbt?.score} />
                  <CWVMetric label="FCP" value={cwv.fcp?.displayValue} score={cwv.fcp?.score} />
                  <CWVMetric label="SI" value={cwv.si?.displayValue} score={cwv.si?.score} />
                </div>
              </Card>

              {/* Scores détaillés */}
              <Card>
                <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">Scores détaillés</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { label: 'Desktop', value: d.pagespeed_performance_desktop, icon: Monitor },
                    { label: 'Mobile', value: d.pagespeed_performance_mobile, icon: Smartphone },
                    { label: 'Accessibilité', value: d.pagespeed_accessibility },
                    { label: 'Bonnes pratiques', value: d.pagespeed_best_practices },
                    { label: 'SEO', value: d.pagespeed_seo },
                  ].map(({ label, value, icon: Icon }) => {
                    const v = Math.round(value || 0);
                    let color = 'text-green-600 bg-green-50 border-green-200';
                    if (v < 50) color = 'text-red-600 bg-red-50 border-red-200';
                    else if (v < 90) color = 'text-amber-600 bg-amber-50 border-amber-200';
                    return (
                      <div key={label} className={`rounded-xl p-4 border ${color} text-center`}>
                        {Icon && <Icon size={16} className="mx-auto mb-1 opacity-60" />}
                        <div className="text-xs text-blue-gray-600 mb-1">{label}</div>
                        <div className="text-2xl font-bold">{v}</div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Opportunités */}
              {perfRecommendations.length > 0 && (
                <Card>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h3 className="font-heading text-base font-semibold text-blue-gray-900 flex items-center gap-2">
                      <AlertTriangle size={18} className="text-amber-500" /> Opportunités d'optimisation ({perfRecommendations.length})
                    </h3>
                    {d?.url && (
                      <a
                        href={`https://pagespeed.web.dev/analysis?url=${encodeURIComponent(d.url)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-accent hover:text-accent/80 hover:bg-accent/5 rounded-lg py-1.5 px-3 transition-colors flex items-center gap-1.5 font-medium border border-accent/20 shrink-0"
                      >
                        <ExternalLink size={14} /> Ouvrir dans Google PageSpeed
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-blue-gray-500 mb-4">
                    Tous les retours Google PageSpeed interprétés pour cette page. Cliquez sur une ligne pour
                    voir l&apos;impact, la consigne et les éléments concernés.
                  </p>
                  <LighthouseFindingList pagespeedRaw={contextData?.pagespeed_raw} />
                </Card>
              )}
            </>
          ) : (
            <Card>
              <div className="text-center py-12">
                <Zap size={32} className="text-blue-gray-300 mx-auto mb-3" />
                <div className="text-blue-gray-600 font-medium mb-2">Données de performance non disponibles</div>
                <div className="text-blue-gray-400 text-sm mb-4">Lancez une analyse pour cette page ou configurez votre clé API PageSpeed.</div>
                {d?.pagespeed_error && (
                  <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm text-left max-w-xl mx-auto">
                    {d.pagespeed_error}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <Button
                    variant="primary"
                    onClick={handleAnalysePerformance}
                    disabled={perfLoading}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw size={16} className={perfLoading ? 'animate-spin' : ''} />
                    {perfLoading ? 'Analyse en cours...' : 'Lancer une analyse performance'}
                  </Button>
                  <button
                    onClick={() => window.history.back()}
                    className="text-sm text-accent hover:underline"
                  >
                    ← Retour à la page Performance
                  </button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ==================== TAB: Résumé & Intention IA ==================== */}
      {activeTab === 'ia' && (
        <div className="flex flex-col gap-6">
          {/* Header + bouton lancer */}
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold text-blue-gray-900 flex items-center gap-2">
              <Lightbulb size={18} className="text-accent" /> Résumé & Intention IA
            </h3>
            <Button variant={iaData ? 'secondary' : 'primary'} onClick={handleAnalyseGemini} disabled={iaLoading} className="flex items-center gap-2">
              <RefreshCw size={14} className={iaLoading ? 'animate-spin' : ''} />
              {iaLoading ? 'Analyse en cours...' : iaData ? 'Relancer l\'analyse' : 'Lancer l\'analyse IA'}
            </Button>
          </div>

          {iaLoading && (
            <Card>
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent mx-auto mb-4" />
                <div className="text-blue-gray-600 font-medium">Gemini analyse la page...</div>
              </div>
            </Card>
          )}

          {iaError && !iaLoading && (
            <Card className="!bg-red-50 !border-red-200">
              <div className="text-red-700 font-medium mb-1">Erreur d'analyse</div>
              <div className="text-red-600 text-sm">{iaError}</div>
            </Card>
          )}

          {/* 1. Résumé (en premier) : Compréhension IA, Pertinence, Compréhension utilisateur */}
          {iaData && !iaLoading && (iaData.analyse_texte?.comprehension_ia || iaData.analyse_texte?.pertinence_contenu || iaData.analyse_texte?.comprehension_utilisateur) && (
            <Card>
              <div className="font-semibold text-blue-gray-900 text-sm mb-4">Résumé de l'analyse</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {iaData.analyse_texte.comprehension_ia && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      <span className="font-medium text-blue-gray-800 text-sm">Compréhension par les moteurs</span>
                    </div>
                    <p className="text-sm text-blue-gray-700 leading-relaxed whitespace-pre-line">{iaData.analyse_texte.comprehension_ia.trim()}</p>
                  </div>
                )}
                {iaData.analyse_texte.pertinence_contenu && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span className="font-medium text-blue-gray-800 text-sm">Pertinence du contenu</span>
                    </div>
                    <p className="text-sm text-blue-gray-700 leading-relaxed whitespace-pre-line">{iaData.analyse_texte.pertinence_contenu.trim()}</p>
                  </div>
                )}
                {iaData.analyse_texte.comprehension_utilisateur && (
                  <div className={iaData.analyse_texte.comprehension_ia && iaData.analyse_texte.pertinence_contenu ? 'lg:col-span-2' : ''}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full" />
                      <span className="font-medium text-blue-gray-800 text-sm">Compréhension utilisateur</span>
                    </div>
                    <p className="text-sm text-blue-gray-700 leading-relaxed whitespace-pre-line">{iaData.analyse_texte.comprehension_utilisateur.trim()}</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* 2. Intention & Ton */}
          {iaData && !iaLoading && iaData.intention_et_ton && (
            <Card className="!border-indigo-200 !bg-indigo-50/30">
              <div className="flex flex-col sm:flex-row gap-4 sm:items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={18} className="text-indigo-600" />
                    <span className="font-semibold text-indigo-900 text-sm">Intention & Ton</span>
                  </div>
                  <div className="text-sm text-indigo-800 leading-relaxed max-w-2xl">
                    {iaData.intention_et_ton.explication}
                  </div>
                </div>
                <div className="flex flex-row sm:flex-col gap-2 flex-shrink-0">
                  <div className="bg-white border border-indigo-100 rounded-lg p-2.5 min-w-[140px]">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-400 mb-0.5">Intention</div>
                    <div className="text-sm font-medium text-indigo-900">{iaData.intention_et_ton.intention_page || iaData.intention_et_ton.intention_recherche || '—'}</div>
                  </div>
                  <div className="bg-white border border-indigo-100 rounded-lg p-2.5 min-w-[140px]">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-400 mb-0.5">Ton</div>
                    <div className="text-sm font-medium text-indigo-900">{iaData.intention_et_ton.ton_page || '—'}</div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* 3. Points forts & faiblesses */}
          {iaData && !iaLoading && iaData.qualite_redactionnelle && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {iaData.qualite_redactionnelle.points_forts?.length > 0 && (
                <Card className="!border-green-200 !bg-green-50/30">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 size={16} className="text-green-600" />
                    <span className="font-semibold text-green-800 text-sm">Points forts ({iaData.qualite_redactionnelle.points_forts.length})</span>
                  </div>
                  <ul className="space-y-1.5">
                    {iaData.qualite_redactionnelle.points_forts.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-green-800">
                        <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              {iaData.qualite_redactionnelle.points_faibles?.length > 0 && (
                <Card className="!border-red-200 !bg-red-50/30">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={16} className="text-red-500" />
                    <span className="font-semibold text-red-800 text-sm">Faiblesses ({iaData.qualite_redactionnelle.points_faibles.length})</span>
                  </div>
                  <ul className="space-y-1.5">
                    {iaData.qualite_redactionnelle.points_faibles.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-red-800">
                        <span className="text-red-500 mt-0.5 flex-shrink-0">✗</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}

          {/* 4. Scores (clarté, lisibilité, etc.) */}
          {iaData && !iaLoading && iaData.qualite_redactionnelle && (
            <Card>
              <div className="text-xs text-blue-gray-500 mb-3">Indicateurs qualité rédactionnelle</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-blue-gray-50 rounded-lg">
                  <div className="text-xs text-blue-gray-500 mb-1">Clarté</div>
                  <div className="text-lg font-bold text-accent">{iaData.qualite_redactionnelle.clarte_texte?.score_clarte ?? '—'}<span className="text-xs text-blue-gray-400">/100</span></div>
                </div>
                <div className="text-center p-3 bg-blue-gray-50 rounded-lg">
                  <div className="text-xs text-blue-gray-500 mb-1">Lisibilité</div>
                  <div className="text-lg font-bold text-accent">{iaData.qualite_redactionnelle.lisibilite?.score_lisibilite ?? '—'}<span className="text-xs text-blue-gray-400">/100</span></div>
                  <div className="text-xs text-blue-gray-400">{iaData.qualite_redactionnelle.lisibilite?.niveau || ''}</div>
                </div>
                <div className="text-center p-3 bg-blue-gray-50 rounded-lg">
                  <div className="text-xs text-blue-gray-500 mb-1">Longueur moy.</div>
                  <div className="text-lg font-bold text-accent">{iaData.qualite_redactionnelle.analyse_phrases?.longueur_moyenne ?? '—'}</div>
                  <div className="text-xs text-blue-gray-400">mots/phrase</div>
                </div>
                <div className="text-center p-3 bg-blue-gray-50 rounded-lg">
                  <div className="text-xs text-blue-gray-500 mb-1">Bourrage mots-clés</div>
                  <div className={`text-lg font-bold ${iaData.qualite_redactionnelle.bourrage_mots_cles?.detection ? 'text-red-600' : 'text-green-600'}`}>
                    {iaData.qualite_redactionnelle.bourrage_mots_cles?.detection ? 'Détecté' : 'OK'}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {!iaData && !iaLoading && !iaError && (
            <Card>
              <div className="text-center py-12">
                <Lightbulb size={32} className="text-blue-gray-300 mx-auto mb-3" />
                <div className="text-blue-gray-600 font-medium mb-2">Aucune analyse IA disponible</div>
                <div className="text-blue-gray-400 text-sm mb-4">Lancez une analyse pour obtenir des insights détaillés sur votre contenu.</div>
                <Button variant="primary" onClick={handleAnalyseGemini} disabled={iaLoading} className="mx-auto flex items-center gap-2">
                  <Lightbulb size={16} /> Lancer l'analyse IA
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ==================== TAB: Structure & Mots-Clés IA ==================== */}
      {activeTab === 'ia' && (
        <div className="flex flex-col gap-6">
          {/* Header + bouton lancer */}
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold text-blue-gray-900 flex items-center gap-2">
              <Lightbulb size={18} className="text-accent" /> Structure & Mots-Clés IA
            </h3>
            <Button variant={iaData ? 'secondary' : 'primary'} onClick={handleAnalyseGemini} disabled={iaLoading} className="flex items-center gap-2">
              <RefreshCw size={14} className={iaLoading ? 'animate-spin' : ''} />
              {iaLoading ? 'Analyse en cours...' : iaData ? 'Relancer l\'analyse' : 'Lancer l\'analyse IA'}
            </Button>
          </div>

          {iaLoading && (
            <Card>
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent mx-auto mb-4" />
                <div className="text-blue-gray-600 font-medium">Gemini analyse la page...</div>
              </div>
            </Card>
          )}

          {iaError && !iaLoading && (
            <Card className="!bg-red-50 !border-red-200">
              <div className="text-red-700 font-medium mb-1">Erreur d'analyse</div>
              <div className="text-red-600 text-sm">{iaError}</div>
            </Card>
          )}

          {/* Comparaison : structure actuelle vs structure idéale (visible dès qu'on a des titres ou une analyse IA) */}
          {(am.headings_list?.length > 0 || d?.h1 || iaData?.structure_ideale?.length > 0) && (
                <Card>
                  <div className="flex items-center gap-2 mb-4">
                    <List size={18} className="text-accent" />
                    <h3 className="font-heading text-base font-semibold text-blue-gray-900">Comparaison des titres</h3>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Colonne gauche : titres actuels sur la page */}
                    <div>
                      <div className="text-xs font-semibold text-blue-gray-500 uppercase tracking-wider mb-2">Structure actuelle</div>
                      <div className="text-xs text-blue-gray-500 mb-3">Titres présents sur la page (ordre du document)</div>
                      <div className="bg-blue-gray-50 rounded-xl p-4 border border-blue-gray-200 min-h-[120px]">
                        {am.headings_list && am.headings_list.length > 0 ? (
                          <div className="space-y-1">
                            {am.headings_list.map((h, i) => {
                              const levelNum = h.level ?? parseInt((h.tag || 'h1').replace('h', ''), 10);
                              const indent = (levelNum - 1) * 24;
                              const levelColors = {
                                1: 'bg-indigo-100 text-indigo-700',
                                2: 'bg-blue-100 text-blue-700',
                                3: 'bg-cyan-100 text-cyan-700',
                                4: 'bg-blue-gray-100 text-blue-gray-700',
                                5: 'bg-blue-gray-100 text-blue-gray-600',
                                6: 'bg-blue-gray-100 text-blue-gray-600',
                              };
                              return (
                                <div key={i} className="group relative" style={{ paddingLeft: `${indent}px` }}>
                                  <div className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-white transition-colors border border-transparent hover:border-blue-gray-200">
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 ${levelColors[levelNum] || 'bg-blue-gray-200 text-blue-gray-700'}`}>
                                      {(h.tag || `h${levelNum}`).toUpperCase()}
                                    </span>
                                    <div className={`text-sm truncate max-w-full ${levelNum === 1 ? 'font-bold text-blue-gray-900' : 'font-medium text-blue-gray-800'}`} title={h.text}>
                                      {h.text || '—'}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : d?.h1 ? (
                          <div className="space-y-1">
                            <div className="flex items-start gap-3 py-2 px-3 rounded-lg bg-white border border-blue-gray-200">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 bg-indigo-100 text-indigo-700">H1</span>
                              <div className="text-sm font-bold text-blue-gray-900">{d.h1}</div>
                            </div>
                            <div className="text-xs text-blue-gray-400 mt-2">Détail complet dans l’onglet SEO Technique → Structure des headings</div>
                          </div>
                        ) : (
                          <div className="text-sm text-blue-gray-400 italic">Aucun titre détecté sur cette page.</div>
                        )}
                      </div>
                    </div>

                    {/* Colonne droite : structure idéale recommandée par l'IA */}
                    <div>
                      <div className="text-xs font-semibold text-accent uppercase tracking-wider mb-2">Structure idéale recommandée</div>
                      <div className="text-xs text-blue-gray-500 mb-3">Hiérarchie proposée par l’IA pour optimiser le SEO</div>
                      <div className="bg-blue-gray-50 rounded-xl p-4 border border-blue-gray-200 min-h-[120px]">
                        {iaData?.structure_ideale && iaData.structure_ideale.length > 0 ? (
                          <div className="space-y-1">
                            {iaData.structure_ideale.map((h, i) => {
                              const levelNum = h.niveau ? parseInt(h.niveau.replace('H', ''), 10) : 2;
                              const indent = (levelNum - 1) * 24;
                              const levelColors = {
                                1: 'bg-indigo-100 text-indigo-700',
                                2: 'bg-blue-100 text-blue-700',
                                3: 'bg-cyan-100 text-cyan-700',
                              };
                              return (
                                <div key={i} className="group relative" style={{ paddingLeft: `${indent}px` }}>
                                  <div className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-white transition-colors border border-transparent hover:border-blue-gray-200">
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 ${levelColors[levelNum] || 'bg-blue-gray-200 text-blue-gray-700'}`}>
                                      {h.niveau || `H${levelNum}`}
                                    </span>
                                    <div className="min-w-0">
                                      <div className={`text-sm ${levelNum === 1 ? 'font-bold text-blue-gray-900' : 'font-medium text-blue-gray-800'}`}>
                                        {h.titre}
                                      </div>
                                      {h.justification && (
                                        <div className="text-xs text-blue-gray-500 mt-1 flex items-start gap-1">
                                          <Sparkles size={12} className="text-accent opacity-70 mt-0.5 flex-shrink-0" />
                                          <span>{h.justification}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-sm text-blue-gray-400 italic">Lancez l’analyse IA pour obtenir une structure recommandée.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
          )}

          {iaData && !iaLoading && (
            <div className="flex flex-col gap-6">
              {/* Mots-clés */}
              {iaData.analyse_mots_cles?.mot_cle_principal && (
                <Card>
                  <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">Analyse des mots-clés</h3>
                  <div className="bg-accent/5 border border-accent/20 rounded-xl p-5 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xl font-bold text-blue-gray-900">{iaData.analyse_mots_cles.mot_cle_principal.mot}</span>
                      <Badge color="accent">{iaData.analyse_mots_cles.mot_cle_principal.densite}% densité</Badge>
                    </div>
                    <div className="text-sm text-blue-gray-600">{iaData.analyse_mots_cles.mot_cle_principal.justification}</div>
                  </div>
                  {iaData.analyse_mots_cles.mots_cles_secondaires?.length > 0 && (
                    <div className="space-y-3">
                      {iaData.analyse_mots_cles.mots_cles_secondaires.map((mc, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-blue-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-blue-gray-900">{mc.mot}</span>
                            <Badge color="accent" className="text-xs">{mc.densite}%</Badge>
                          </div>
                          <span className="text-xs text-blue-gray-500">{mc.pertinence}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== TAB: Plan d'action IA ==================== */}
      {activeTab === 'plan' && (
        <div className="flex flex-col gap-6">
          {/* Header + bouton lancer */}
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold text-blue-gray-900 flex items-center gap-2">
              <Lightbulb size={18} className="text-accent" /> Plan d'action IA
            </h3>
            <Button variant={iaData ? 'secondary' : 'primary'} onClick={handleAnalyseGemini} disabled={iaLoading} className="flex items-center gap-2">
              <RefreshCw size={14} className={iaLoading ? 'animate-spin' : ''} />
              {iaLoading ? 'Analyse en cours...' : iaData ? 'Relancer l\'analyse' : 'Lancer l\'analyse IA'}
            </Button>
          </div>

          {iaLoading && (
            <Card>
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent mx-auto mb-4" />
                <div className="text-blue-gray-600 font-medium">Gemini analyse la page...</div>
              </div>
            </Card>
          )}

          {iaError && !iaLoading && (
            <Card className="!bg-red-50 !border-red-200">
              <div className="text-red-700 font-medium mb-1">Erreur d'analyse</div>
              <div className="text-red-600 text-sm">{iaError}</div>
            </Card>
          )}

          {iaData && !iaLoading && (
            <>
              {/* Qualité rédactionnelle — Recommandations plan */}
              {iaData.qualite_redactionnelle && (
                <Card>
                  <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">Plan d'action priorisé</h3>

                  {/* Categorized and prioritized action plan */}
                  {(() => {
                    // Fallback to old flat format if plan_action doesn't exist
                    if (!iaData.qualite_redactionnelle.plan_action || iaData.qualite_redactionnelle.plan_action.length === 0) {
                      const allRecs = [
                        ...(iaData.qualite_redactionnelle.recommandations_generales || []),
                        ...(iaData.analyse_mots_cles?.recommandations_techniques?.map(r => r.justification || `${r.mot_cle} (${r.densite_suggeree}%)`) || []),
                        ...(iaData.analyse_mots_cles?.recommandations_sectorielles?.map(r => r.justification || `${r.mot_cle} (${r.densite_suggeree}%)`) || []),
                      ];
                      if (allRecs.length === 0) return null;
                      return (
                        <div className="space-y-2">
                          {allRecs.map((r, i) => {
                            const key = `plan-${i}`;
                            const done = completedRecommendations.has(key);
                            return (
                              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${done ? 'bg-green-50/50 border-green-200/60' : 'bg-blue-gray-50/50 border-blue-gray-200/40 hover:border-accent/30'}`}>
                                <button onClick={() => toggleRecommendation('plan', i)} className="mt-0.5 flex-shrink-0">
                                  {done ? <CheckCircle2 className="text-green-600" size={18} /> : <Circle className="text-blue-gray-400 hover:text-accent transition-colors" size={18} />}
                                </button>
                                <span className={`text-sm leading-relaxed ${done ? 'text-green-700 line-through' : 'text-blue-gray-700'}`}>{r}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    // Display the new structured plan_action
                    const categories = [...new Set(iaData.qualite_redactionnelle.plan_action.map(a => a.categorie || 'Autre'))];

                    const getPriorityColor = (priorite) => {
                      const p = (priorite || '').toLowerCase();
                      if (p.includes('haute') || p.includes('high')) return 'bg-red-50 text-red-700 border-red-200';
                      if (p.includes('moyenne') || p.includes('medium')) return 'bg-amber-50 text-amber-700 border-amber-200';
                      return 'bg-blue-50 text-blue-700 border-blue-200';
                    };

                    return (
                      <div className="space-y-6">
                        {categories.map(cat => {
                          const actions = iaData.qualite_redactionnelle.plan_action.filter(a => (a.categorie || 'Autre') === cat);
                          // Sort by priority (Haute > Moyenne > Basse)
                          actions.sort((a, b) => {
                            const pA = (a.priorite || '').toLowerCase();
                            const pB = (b.priorite || '').toLowerCase();
                            const weight = p => p.includes('haute') ? 3 : p.includes('moyenne') ? 2 : 1;
                            return weight(pB) - weight(pA);
                          });

                          return (
                            <div key={cat} className="space-y-3">
                              <h4 className="text-sm font-semibold text-blue-gray-700 uppercase tracking-wide border-b border-blue-gray-100 pb-2">{cat}</h4>
                              <div className="space-y-2">
                                {actions.map((item, i) => {
                                  const key = `action-${cat}-${i}`;
                                  const done = completedRecommendations.has(key);
                                  return (
                                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${done ? 'bg-green-50/50 border-green-200/60' : 'bg-white border-blue-gray-200/80 hover:border-accent/40 shadow-sm'}`}>
                                      <button onClick={() => toggleRecommendation('action', `${cat}-${i}`)} className="mt-0.5 flex-shrink-0">
                                        {done ? <CheckCircle2 className="text-green-600" size={18} /> : <Circle className="text-blue-gray-300 hover:text-accent transition-colors" size={18} />}
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <div className={`text-sm leading-relaxed mb-1.5 ${done ? 'text-green-700 line-through' : 'text-blue-gray-800 font-medium'}`}>{item.action}</div>
                                        <Badge className={`text-[10px] uppercase font-bold px-2 py-0.5 border ${getPriorityColor(item.priorite)}`}>
                                          Priorité {item.priorite || 'Basse'}
                                        </Badge>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </Card>
              )}
            </>
          )}

          {!iaData && !iaLoading && !iaError && (
            <Card>
              <div className="text-center py-12">
                <Lightbulb size={32} className="text-blue-gray-300 mx-auto mb-3" />
                <div className="text-blue-gray-600 font-medium mb-2">Aucune analyse IA disponible</div>
                <div className="text-blue-gray-400 text-sm mb-4">Lancez une analyse pour obtenir des insights détaillés sur votre contenu.</div>
                <Button variant="primary" onClick={handleAnalyseGemini} disabled={iaLoading} className="mx-auto flex items-center gap-2">
                  <Lightbulb size={16} /> Lancer l'analyse IA
                </Button>
              </div>
            </Card>
          )}
        </div>
      )
      }

      {/* ==================== TAB: Suggestions ==================== */}
      {
        activeTab === 'plan' && (
          <div className="flex flex-col gap-6">
            {iaData ? (
              <>
                {[
                  { title: 'Suggestions de titre', items: titreList, subtitle: 'Titres optimisés pour le SEO (recommandation Google : 50–60 caractères)', charRange: { min: 50, max: 60 }, block: 'titre' },
                  { title: 'Suggestions de meta description', items: metaList, subtitle: 'Meta descriptions attractives (recommandation Google : 150–160 caractères)', charRange: { min: 150, max: 160 }, block: 'meta_description' },
                  { title: 'Suggestions de H1', items: h1List, subtitle: 'H1 optimisés', block: 'h1' },
                  { title: 'Suggestions d\'alt images', items: altList, subtitle: 'Attributs alt descriptifs' },
                ].map(({ title, items, subtitle, charRange, block }) => (
                  <Card key={title}>
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="min-w-0">
                        <h3 className="font-heading text-base font-semibold text-blue-gray-900">{title}</h3>
                        <div className="text-sm text-blue-gray-400 mt-1">{subtitle}</div>
                      </div>
                      {block && (
                        <Button
                          variant="secondary"
                          onClick={() => handleRegenerateBlock(block)}
                          disabled={!!regeneratingBlock}
                          className="flex items-center gap-1.5 flex-shrink-0"
                        >
                          <RefreshCw size={14} className={regeneratingBlock === block ? 'animate-spin' : ''} />
                          {regeneratingBlock === block ? 'Régénération...' : 'Regénérer'}
                        </Button>
                      )}
                    </div>
                    {items.length > 0 ? (
                      <div className="space-y-3">
                        {items.map((item, i) => {
                          const len = typeof item === 'string' ? item.length : 0;
                          let statusLabel = null;
                          let statusClass = '';
                          if (charRange) {
                            if (len >= charRange.min && len <= charRange.max) {
                              statusLabel = 'Conforme';
                              statusClass = 'text-emerald-600 bg-emerald-50';
                            } else if (len < charRange.min) {
                              statusLabel = 'Trop court';
                              statusClass = 'text-amber-600 bg-amber-50';
                            } else {
                              statusLabel = 'Trop long';
                              statusClass = 'text-red-600 bg-red-50';
                            }
                          }
                          return (
                            <div key={i} className="flex items-start justify-between gap-3 bg-blue-gray-50 rounded-lg p-4 border border-blue-gray-200 hover:border-accent/40 transition-colors">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-blue-gray-900">{item}</div>
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-blue-gray-500">{len} caractère{len !== 1 ? 's' : ''}</span>
                                  {statusLabel && (
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${statusClass}`}>{statusLabel}</span>
                                  )}
                                </div>
                              </div>
                              <button onClick={() => handleCopy(item)} className="p-1.5 rounded text-blue-gray-400 hover:text-accent hover:bg-white transition-colors flex-shrink-0" title="Copier">
                                <Copy size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-blue-gray-400 italic py-4 text-center">Aucune suggestion disponible</div>
                    )}
                  </Card>
                ))}

                {/* Recommandations techniques et sectorielles */}
                {(iaData.analyse_mots_cles?.recommandations_techniques?.length > 0 || iaData.analyse_mots_cles?.recommandations_sectorielles?.length > 0) && (
                  <Card>
                    <h3 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">Recommandations mots-clés</h3>
                    <div className="space-y-3">
                      {[...(iaData.analyse_mots_cles?.recommandations_techniques || []), ...(iaData.analyse_mots_cles?.recommandations_sectorielles || [])].map((r, i) => (
                        <div key={i} className="flex items-start justify-between gap-3 p-4 bg-blue-gray-50 rounded-lg border border-blue-gray-200">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-blue-gray-900">{r.mot_cle}</span>
                              <Badge color="accent" className="text-xs">{r.densite_suggeree}%</Badge>
                              <span className="text-xs text-blue-gray-400 capitalize">{r.type}</span>
                            </div>
                            <div className="text-sm text-blue-gray-600">{r.justification}</div>
                          </div>
                          <button onClick={() => handleCopy(r.mot_cle)} className="p-1.5 rounded text-blue-gray-400 hover:text-accent hover:bg-white transition-colors flex-shrink-0" title="Copier">
                            <Copy size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <div className="text-center py-12">
                  <Lightbulb size={32} className="text-blue-gray-300 mx-auto mb-3" />
                  <div className="text-blue-gray-600 font-medium mb-2">Lancez d'abord l'analyse IA</div>
                  <div className="text-blue-gray-400 text-sm mb-4">Les suggestions sont générées par Gemini AI.</div>
                  <Button variant="primary" onClick={handleAnalyseGemini} disabled={iaLoading} className="mx-auto flex items-center gap-2">
                    <Lightbulb size={16} /> Lancer l'analyse IA
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )
      }
    </div>
  );
}
